/**
 * Small inline icons for icon-only buttons. Stroked with `currentColor` so they
 * pick up the button's colour in every variant and hover state, same approach
 * as the chevron in `Dropdown`. Purely decorative — the button carries the
 * accessible name via aria-label.
 */

const base = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Svg({ children, size = 15 }: { children: React.ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      style={{ flex: 'none' }}
    >
      {children}
    </svg>
  );
}

/** Pencil — "bewerken". */
export function PencilIcon({ size }: { size?: number }) {
  return (
    <Svg size={size}>
      <path {...base} d="M11.9 2.4a1.3 1.3 0 0 1 1.8 1.8l-7.9 7.9-2.6.8.8-2.6 7.9-7.9z" />
      <path {...base} d="M10.4 3.9l1.8 1.8" />
    </Svg>
  );
}

/** Calculator — "berekenen". */
export function CalculatorIcon({ size }: { size?: number }) {
  return (
    <Svg size={size}>
      <rect {...base} x="3.2" y="1.8" width="9.6" height="12.4" rx="1.3" />
      <path {...base} d="M5.4 4.6h5.2" />
      <g fill="currentColor" stroke="none">
        <circle cx="5.7" cy="8.1" r="0.75" />
        <circle cx="8" cy="8.1" r="0.75" />
        <circle cx="10.3" cy="8.1" r="0.75" />
        <circle cx="5.7" cy="11.1" r="0.75" />
        <circle cx="8" cy="11.1" r="0.75" />
        <circle cx="10.3" cy="11.1" r="0.75" />
      </g>
    </Svg>
  );
}

/** Circled i — "toelichting". */
export function InfoIcon({ size }: { size?: number }) {
  return (
    <Svg size={size}>
      <circle {...base} cx="8" cy="8" r="6.2" />
      <path {...base} d="M8 7.4v4" />
      <circle cx="8" cy="4.9" r="0.85" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Plus — "toevoegen". */
export function PlusIcon({ size }: { size?: number }) {
  return (
    <Svg size={size}>
      <path {...base} d="M8 3.4v9.2M3.4 8h9.2" />
    </Svg>
  );
}

/**
 * Pijl naar links — "terug". Als svg en niet als `←`, want een glyph staat op de
 * tekstregel en hangt naast een label net te hoog.
 *
 * Eigen svg in plaats van de `Svg`-helper hierboven: deze staat náást een label
 * in plaats van alleen in een icoonknop, en heeft daarom een zwaardere lijn
 * nodig om even sterk te lezen als de tekst ernaast.
 */
export function ArrowLeftIcon({ size = 15 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: 'none' }}
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}
